import React from 'react';
import { FadeIn } from '../ui/FadeIn';
import { CTAButton } from '../ui/CTAButton';
import { Sparkles, CheckCircle2, Shield, Lock, Users, Zap, Headphones } from 'lucide-react';
export function Hero() {
  return <section className="relative pt-16 pb-20 md:pt-20 md:pb-28 overflow-hidden bg-navy" data-id="element-52">
      {/* Multi-color neon background */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gold/10 rounded-full blur-[120px] pointer-events-none" data-id="element-53"></div>
      <div className="absolute top-20 left-0 w-96 h-96 bg-magenta/15 rounded-full blur-[100px] pointer-events-none" data-id="element-54"></div>
      <div className="absolute top-1/3 right-0 w-96 h-96 bg-cyan/12 rounded-full blur-[100px] pointer-events-none" data-id="element-55"></div>
      <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-violet/12 rounded-full blur-[100px] pointer-events-none" data-id="element-56"></div>
      <div className="absolute bottom-10 right-20 w-72 h-72 bg-lime/8 rounded-full blur-[80px] pointer-events-none" data-id="element-57"></div>
      <div className="absolute top-40 left-10 text-cyan/[0.08] text-9xl font-serif select-none pointer-events-none" data-id="element-58">
        ♪
      </div>
      <div className="absolute bottom-20 right-10 text-magenta/[0.08] text-8xl font-serif select-none pointer-events-none" data-id="element-59">
        ♫
      </div>

      <div className="container mx-auto px-4 max-w-5xl relative z-10" data-id="element-60">
        <div className="flex flex-col items-center text-center" data-id="element-61">
          {/* Product Badge */}
          <FadeIn data-id="element-62">
            <div className="inline-flex items-center gap-2 bg-magenta/10 border border-magenta/40 text-magenta-light px-5 py-2 rounded-full text-sm md:text-base font-semibold mb-8 backdrop-blur-sm shadow-neon-magenta" data-id="element-63">
              <Sparkles className="w-4 h-4 drop-shadow-glow-magenta" data-id="element-64" />
              MUSIC PRO KIT · Academia Musical
            </div>
          </FadeIn>

          {/* Headline */}
          <FadeIn delay={0.1} data-id="element-65">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-beige mb-6 leading-[1.05] max-w-4xl mx-auto tracking-tight" data-id="element-66">
              ¡Tocá tu primera{' '}
              <span className="text-gold drop-shadow-glow-gold" data-id="element-67">canción</span>{' '}
              en{' '}
              <span className="text-magenta-light underline decoration-magenta/40 underline-offset-4 decoration-[3px] drop-shadow-glow-magenta" data-id="element-68">
                48 horas
              </span>
              !
            </h1>
          </FadeIn>

          {/* Mockup with diffused purple halo */}
          <FadeIn delay={0.3} className="w-full max-w-xl mx-auto mb-6 mt-4" data-id="element-69">
            <div className="relative" data-id="element-70">
              {/* Diffused purple halo that blends with the page */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[130%] pointer-events-none" style={{
              background: 'radial-gradient(ellipse at center, rgba(168, 85, 247, 0.5) 0%, rgba(124, 58, 237, 0.32) 22%, rgba(76, 29, 149, 0.18) 45%, transparent 72%)',
              filter: 'blur(30px)'
            }} data-id="element-71"></div>
              <div className="absolute top-1/4 left-1/4 w-44 h-44 bg-magenta/30 blur-[70px] rounded-full pointer-events-none" data-id="element-72"></div>
              <div className="absolute bottom-1/4 right-1/4 w-44 h-44 bg-cyan/20 blur-[70px] rounded-full pointer-events-none" data-id="element-73"></div>

              <img src="https://cdn.magicpatterns.com/uploads/kh3ob9wM75JahZJiaxSMYz/Untitled_design_(1).png" alt="MUSIC PRO KIT - Academia musical multi-instrumental" className="relative z-10 w-[85%] h-auto drop-shadow-[0_25px_50px_rgba(0,0,0,0.6)] mx-auto" data-id="element-74" />
            </div>
          </FadeIn>

          {/* Subtitle */}
          <FadeIn delay={0.4} data-id="element-75">
            <p className="text-xl md:text-2xl text-beige/90 mb-8 max-w-2xl mx-auto font-medium" data-id="element-76">
              Tu formación musical completa{' '}
              <span className="text-gold font-semibold drop-shadow-glow-gold" data-id="element-77">
                en un solo lugar
              </span>
            </p>
          </FadeIn>

          {/* Tomos & Bonos tagline */}
          <FadeIn delay={0.45} data-id="element-78">
            <p className="text-lg md:text-xl font-bold text-beige mb-5 text-center" data-id="element-79">
              <span className="text-cyan drop-shadow-glow-cyan" data-id="element-80">6 Tomos</span>
              <span className="text-beige/40 mx-2" data-id="element-81">·</span>
              <span className="text-magenta-light drop-shadow-glow-magenta" data-id="element-82">
                7 Bonos
              </span>
              <span className="text-beige/40 mx-2" data-id="element-83">·</span>
              <span className="text-gold drop-shadow-glow-gold" data-id="element-84">
                Listo para usar
              </span>
            </p>
          </FadeIn>

          {/* Quick benefits */}
          <FadeIn delay={0.5} data-id="element-85">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mb-6 text-sm md:text-base" data-id="element-86">
              <div className="flex items-center gap-2 text-beige/90" data-id="element-87">
                <CheckCircle2 className="w-5 h-5 text-gold drop-shadow-glow-gold" data-id="element-88" />
                <span data-id="element-89">PDF Digital</span>
              </div>
              <div className="flex items-center gap-2 text-beige/90" data-id="element-90">
                <CheckCircle2 className="w-5 h-5 text-magenta-light drop-shadow-glow-magenta" data-id="element-91" />
                <span data-id="element-92">Acceso Inmediato</span>
              </div>
              <div className="flex items-center gap-2 text-beige/90" data-id="element-93">
                <CheckCircle2 className="w-5 h-5 text-cyan drop-shadow-glow-cyan" data-id="element-94" />
                <span data-id="element-95">Todos los niveles</span>
              </div>
            </div>
          </FadeIn>

          {/* CTA */}
          <FadeIn delay={0.6} className="flex flex-col items-center w-full max-w-md mx-auto" data-id="element-96">
            <CTAButton fullWidth className="mb-4" data-id="element-97">
              QUIERO MI MUSIC PRO KIT AHORA
            </CTAButton>
            <p className="text-sm font-medium text-beige/60 flex items-center gap-2 mb-6" data-id="element-98">
              <span className="w-2 h-2 rounded-full bg-lime drop-shadow-glow-lime animate-pulse" data-id="element-99"></span>
              Acceso inmediato – empezás hoy mismo
            </p>

            {/* Trust badges - fluorescent style like reference */}
            <div className="grid grid-cols-2 gap-3 w-full max-w-lg mx-auto text-sm" data-id="element-100">
              <div className="flex items-center gap-2.5 bg-magenta/8 border border-magenta/30 px-4 py-3 rounded-xl" data-id="element-101">
                <Zap className="w-4 h-4 text-magenta-light drop-shadow-glow-magenta flex-shrink-0" data-id="element-102" />
                <span className="text-beige/90 font-medium" data-id="element-103">
                  Acceso inmediato
                </span>
              </div>
              <div className="flex items-center gap-2.5 bg-cyan/8 border border-cyan/30 px-4 py-3 rounded-xl" data-id="element-104">
                <Headphones className="w-4 h-4 text-cyan drop-shadow-glow-cyan flex-shrink-0" data-id="element-105" />
                <span className="text-beige/90 font-medium" data-id="element-106">PDF Digital</span>
              </div>
              <div className="flex items-center gap-2.5 bg-violet/8 border border-violet/30 px-4 py-3 rounded-xl" data-id="element-107">
                <Lock className="w-4 h-4 text-violet-light drop-shadow-glow-violet flex-shrink-0" data-id="element-108" />
                <span className="text-beige/90 font-medium" data-id="element-109">Pago seguro</span>
              </div>
              <div className="flex items-center gap-2.5 bg-gold/8 border border-gold/30 px-4 py-3 rounded-xl" data-id="element-110">
                <Shield className="w-4 h-4 text-gold drop-shadow-glow-gold flex-shrink-0" data-id="element-111" />
                <span className="text-beige/90 font-medium" data-id="element-112">
                  Garantía 7 días
                </span>
              </div>
              <div className="flex items-center gap-2.5 bg-lime/8 border border-lime/30 px-4 py-3 rounded-xl col-span-2 justify-center" data-id="element-113">
                <Users className="w-4 h-4 text-lime drop-shadow-glow-lime flex-shrink-0" data-id="element-114" />
                <span className="text-beige/90 font-medium" data-id="element-115">+500 alumnos</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}